import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Check, X, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';

let toastId = 0;
let addToast = () => {};
let removeToast = () => {};

const Toast = ({ id, message, type = 'success', onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Loading toasts don't auto-dismiss
    if (type === 'loading') return;

    const timer = setTimeout(() => {
      handleClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [type]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const variants = {
    success: {
      bg: "bg-gray-900",
      icon: <Check className="w-4 h-4 text-white" />,
      iconBg: "bg-green-500",
    },
    error: {
      bg: "bg-gray-900",
      icon: <X className="w-4 h-4 text-white" />,
      iconBg: "bg-red-500",
    },
    info: {
      bg: "bg-gray-900",
      icon: <AlertCircle className="w-4 h-4 text-white" />,
      iconBg: "bg-pink-500",
    },
    warning: {
      bg: "bg-gray-900",
      icon: <AlertTriangle className="w-4 h-4 text-white" />,
      iconBg: "bg-amber-500",
    },
    loading: {
      bg: "bg-gray-900",
      icon: <Loader2 className="w-4 h-4 text-white animate-spin" />,
      iconBg: "bg-pink-500",
    }
  };

  const variant = variants[type] || variants.success;

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl
        pointer-events-auto backdrop-blur-sm
        ${variant.bg} text-white
        transform transition-all duration-300 ease-out
        ${isExiting
          ? 'translate-x-full opacity-0'
          : 'translate-x-0 opacity-100 animate-toast-in'
        }
      `}
      style={{ minWidth: '200px', maxWidth: '320px' }}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 w-6 h-6 rounded-full ${variant.iconBg} flex items-center justify-center`}>
        {variant.icon}
      </div>

      {/* Message */}
      <p className="text-sm font-medium text-white/95 flex-1">{message}</p>

      {/* Close button - only for loading or if needed */}
      {type === 'loading' && (
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="w-3.5 h-3.5 text-white/60" />
        </button>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  addToast = (message, type) => {
    const id = toastId++;
    setToasts(prev => [...prev, { id, message, type }]);
    return id;
  };

  removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  window.__updateToast = (id, message, type) => {
    setToasts(prev => prev.map(toast =>
      toast.id === id ? { ...toast, message, type } : toast
    ));
  };

  return (
    <>
      {/* Toast container - bottom right */}
      <div
        className="fixed bottom-4 right-4 flex flex-col-reverse gap-2 pointer-events-none z-[99999]"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes toastIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-toast-in {
          animation: toastIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

// Mount container
let containerMounted = false;
const mountContainer = () => {
  if (containerMounted) return;

  const containerDiv = document.createElement('div');
  containerDiv.id = 'toast-container';
  document.body.appendChild(containerDiv);

  const root = createRoot(containerDiv);
  root.render(<ToastContainer />);

  containerMounted = true;
};

let lastToastId = null;

export const toast = {
  show: (message, type = 'success') => {
    if (!containerMounted) {
      mountContainer();
    }
    lastToastId = addToast(message, type);
    return lastToastId;
  },
  success: (message) => toast.show(message, 'success'),
  error: (message) => toast.show(message, 'error'),
  info: (message) => toast.show(message, 'info'),
  warning: (message) => toast.show(message, 'warning'),
  loading: (message) => toast.show(message, 'loading'),
  dismiss: (id) => {
    if (id !== undefined) {
      removeToast(id);
    } else if (lastToastId !== null) {
      removeToast(lastToastId);
    }
  },
  update: (id, message, type) => {
    if (window.__updateToast) {
      window.__updateToast(id, message, type);
    }
  }
};

export default toast;
